/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useRef } from "react";

import { FileRejection, useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import styled from "styled-components";

import { Plate } from "src/classes/plate/Plate";
import { ACCEPTED_FILE_TYPES } from "src/lib/constants";
import { TOAST_DEFAULT_ARGS } from "src/lib/constants";

interface Props {
  plate: Plate;
  children: React.ReactNode | React.ReactNode[];
}

export function Uploader({ plate, children }: Props) {
  const dropAreaRef = useRef();

  const onDropRejected = (rejectedFiles: FileRejection[]) => {
    const oversize = rejectedFiles.filter((fileRejection) => fileRejection.file.size > 500000000);
    const errors = rejectedFiles.map((fileRejection) => fileRejection.errors.map((error) => error.message));
    const nonOversizeRejections = errors.filter((errorMessage) => !errorMessage.includes("file-too-large"));
    if (oversize.length) {
      toast.error("Max file size is 500MB", TOAST_DEFAULT_ARGS);
    } else if (nonOversizeRejections.length) {
      toast.error("File not supported", TOAST_DEFAULT_ARGS);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    multiple: true,
    accept: ACCEPTED_FILE_TYPES,
    noClick: true,
    maxSize: 500000000,
    onDropAccepted: plate.onDrop,
    onDropRejected,
  });

  return (
    <StyledDropArea {...getRootProps()} ref={dropAreaRef}>
      <input {...getInputProps()} type="file" name="file" id="plate_upload_input" />
      {children}
    </StyledDropArea>
  );
}

const StyledDropArea = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  :focus {
    outline: none;
  }
`;
